import { useEffect, useState } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    ArrowUpTrayIcon,
    DocumentTextIcon,
    PaperClipIcon,
    PhotoIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { FileDropzone } from './FileDropzone';

// PO drawer attachments section matching the Eadin design system —
// "Attachments (N)" heading, Upload toggle with compact dropzone, and
// hover-revealed remove buttons with a confirmation dialog.
export const AttachmentsSection = ({
    access,
    initialFiles = [],
    referenceType,
    referenceId,
    onFilesChange,
    testId
}) => {
    const [files, setFiles] = useState(initialFiles || []);
    const [showUpload, setShowUpload] = useState(false);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [deleteFile, setDeleteFile] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        setFiles(initialFiles || []);
    }, [initialFiles]);

    useEffect(() => {
        setShowUpload(false);
        setPendingFiles([]);
        setDeleteFile(null);
    }, [referenceId]);

    const submitFiles = async () => {
        if (pendingFiles.length === 0) return;
        setUploading(true);
        try {
            const uploaded = await Promise.all(pendingFiles.map(async (file) => {
                const formData = new FormData();
                formData.append('file', file);
                if (referenceType) formData.append('referenceType', referenceType);
                if (referenceId) formData.append('referenceId', referenceId);
                const response = await axios.post('/api/v1/files/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                return response.data.file;
            }));
            const updated = [...files, ...uploaded];
            setFiles(updated);
            onFilesChange?.(updated);
            toast.success(`${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''} attached`, { position: 'bottom-right' });
            setPendingFiles([]);
            setShowUpload(false);
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error('Failed to upload attachments', { position: 'bottom-right' });
        } finally {
            setUploading(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteFile) return;
        setDeleting(true);
        try {
            await axios.delete(`/api/v1/files/delete/${deleteFile.id}`);
            const updated = files.filter((file) => file.id !== deleteFile.id);
            setFiles(updated);
            onFilesChange?.(updated);
            toast.success(`Attachment "${deleteFile.original_name}" removed`, { position: 'bottom-right' });
        } catch (error) {
            console.error('Error deleting file:', error);
            toast.error('Failed to remove attachment', { position: 'bottom-right' });
        } finally {
            setDeleting(false);
            setDeleteFile(null);
        }
    };

    const handleDownload = async (url, fileName) => {
        try {
            const response = await fetch(url, { mode: 'cors' });
            const blob = await response.blob();
            saveAs(blob, fileName);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    return (
        <div data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-bold flex items-center gap-1.5">
                    <PaperClipIcon className="h-4 w-4" /> Attachments ({files.length})
                </h3>
                {access === 'edit' && (
                    <button
                        onClick={() => setShowUpload((v) => !v)}
                        className="flex items-center gap-1.5 h-7 px-3 rounded-md border border-gray-300 text-xs font-medium hover:bg-gray-50 transition-colors"
                    >
                        <ArrowUpTrayIcon className="h-3 w-3" /> Upload
                    </button>
                )}
            </div>
            {showUpload && (
                <div className="mb-3 space-y-2">
                    <FileDropzone files={pendingFiles} onChange={setPendingFiles} compact />
                    {pendingFiles.length > 0 && (
                        <div className="flex gap-2">
                            <button
                                onClick={submitFiles}
                                disabled={uploading}
                                className="flex items-center gap-1.5 h-7 px-3 rounded-md bg-primary text-white text-xs font-medium hover:bg-gray-800 disabled:opacity-50"
                            >
                                {uploading && <ArrowPathIcon className="h-3 w-3 animate-spin" />}
                                Attach {pendingFiles.length} File{pendingFiles.length > 1 ? 's' : ''}
                            </button>
                            <button
                                onClick={() => { setPendingFiles([]); setShowUpload(false); }}
                                className="h-7 px-3 rounded-md border border-gray-300 text-xs font-medium hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            )}
            {files.length === 0 && !showUpload ? (
                <div className="text-sm text-gray-500 py-1">No attachments yet.</div>
            ) : (
                <div className="space-y-1">
                    {files.map((file, index) => (
                        <div key={`${file.id}_${index}`} className="group flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-gray-50 transition-colors">
                            <a
                                href={encodeURI(file.location_file)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 min-w-0 flex-1 text-left hover:underline underline-offset-2"
                                title={`Open ${file.original_name}`}
                            >
                                {(file.content_type || '').startsWith('image/') ? (
                                    <PhotoIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                ) : (
                                    <DocumentTextIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                )}
                                <span className="truncate">{file.original_name}</span>
                            </a>
                            <button
                                type="button"
                                onClick={() => handleDownload(encodeURI(file.location_file), file.original_name)}
                                className="p-1 rounded hover:bg-gray-100 text-gray-400 opacity-60 group-hover:opacity-100 transition-opacity"
                                aria-label={`Download ${file.original_name}`}
                                title="Download attachment"
                            >
                                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                            </button>
                            {access === 'edit' && (
                                <button
                                    type="button"
                                    onClick={() => setDeleteFile(file)}
                                    className="p-1 rounded hover:bg-gray-100 text-gray-400 opacity-60 group-hover:opacity-100 transition-opacity"
                                    aria-label={`Remove ${file.original_name}`}
                                    title="Remove attachment"
                                >
                                    <XMarkIcon className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {deleteFile && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-gray-700 opacity-40" onClick={() => setDeleteFile(null)} />
                    <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl z-[71] p-5">
                        <h3 className="text-lg font-bold mb-2">You are about to remove an attachment</h3>
                        <p className="text-sm text-gray-700">
                            "{deleteFile.original_name}" will be removed from this PO. Do you wish to proceed?
                        </p>
                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                onClick={() => setDeleteFile(null)}
                                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                            >
                                No, go back
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={deleting}
                                className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-gray-800 disabled:opacity-50"
                            >
                                Yes, proceed
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
